import { useEffect, useState } from 'react'
import { useAxios } from '@/contexts/AxiosProvider'
import { useHall } from '@/contexts/HallProvider'
import { useAzureTts } from '@/contexts/AzureTtsProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import { JudgmentReadingControl } from '@/components/sequence/judgment-reading-control'
import Loader from '@/components/loaders/Loader'
import ReactMarkdown from 'react-markdown'
import { SpeakingRole, Winner } from '@db8/shared'
import { usePoll } from '@/contexts/PollProvider'

export const JudgmentPanel = () => {
  const { transcriptions, hall, handleJudgment, judgment, isOwner, setHallMetadata, hallMetadata } =
    useHall()
  const { setShowWinner } = usePoll()
  const { isModerator } = useHallUsers()
  const { generateAudio, playReading, pauseReading, restartReading, playerReady } = useAzureTts()
  const axios = useAxios()
  const [isLoading, setIsLoading] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const { setSideBarMode } = useMeetingAppContext()

  const determineWinner = (
    text: string,
    affirmingTeam: string,
    dissentingTeam: string,
  ): SpeakingRole | null => {
    const pattern = new RegExp(`\\*\\*(${affirmingTeam}|${dissentingTeam}) wins\\*\\*`, 'i')
    const match = text.match(pattern)
    if (!match) return null

    const winningTeam = match[1].toLowerCase()
    if (winningTeam === affirmingTeam.toLowerCase())
      setHallMetadata({ winningTeam: Winner.Affirmative, debateComplete: true })
    else if (winningTeam === dissentingTeam.toLowerCase())
      setHallMetadata({ winningTeam: Winner.Negative, debateComplete: true })
    else setHallMetadata({ winningTeam: Winner.Tie, debateComplete: true })

    setShowWinner({
      val: true,
      initialized: false,
    })
  }

  const getJudgment = async () => {
    const res = await axios.post('/ai/judgment', {
      resolution: hall.topic,
      transcriptions,
      hall,
    })

    const raw = res.data.text
    handleJudgment(raw)
    determineWinner(raw, hall.affirmingTeamName, hall.dissentingTeamName)

    await generateAudio(raw)
  }

  useEffect(() => {
    if (!judgment && isModerator) getJudgment()
    else if (!judgment && !isModerator) setSideBarMode(null)
    else setIsLoading(false)
  }, [judgment])

  return (
    <div className="overflow-y-scroll h-full pb-14 relative noScrollbar">
      {isLoading ? (
        <div className="h-full w-full flex justify-center items-center">
          <Loader />
        </div>
      ) : (
        <div className="prose prose-sm dark:prose-invert px-4 py-2">
          <ReactMarkdown>{judgment}</ReactMarkdown>
        </div>
      )}

      {isOwner && (
        <JudgmentReadingControl
          restartReading={() => {
            restartReading()
            setIsPlaying(true)
          }}
          playReading={() => {
            playReading()
            setIsPlaying(true)
          }}
          pauseReading={() => {
            pauseReading()
            setIsPlaying(false)
          }}
          playerReady={playerReady}
          isPlaying={isPlaying}
        />
      )}
    </div>
  )
}
